import { useState, useCallback } from 'react';
import Fuse from 'fuse.js';
import JSZip from 'jszip';

const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

// Fuse.js scores: 0 = perfect match, 1 = no match.
// AUTO_MAP_THRESHOLD: score at or below this → silently auto-map (no user input needed).
// SUGGEST_THRESHOLD:  score at or below this → show as a candidate for user to pick.
const AUTO_MAP_THRESHOLD = 0.2;
const SUGGEST_THRESHOLD = 0.3;

// Strip file extension, replace separators (_, -, .) with spaces, lowercase.
// "Full_Stack_Engineer_EMEA.pdf" → "full stack engineer emea"
const stripExtension = (filename) => filename.replace(/\.[^/.]+$/, '');

const normalise = (str) =>
  stripExtension(str)
    .replace(/[_\-\.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

// Build initialism from job title, skipping filler words.
// "Senior Vice President of Engineering" → "SVP" (skips "of")
const buildAbbreviation = (jobTitle) => {
  const SKIP = new Set(['of', 'and', 'the', 'for', 'in', 'at', 'to', 'a', 'an', '&']);
  return jobTitle
    .split(/\s+/)
    .filter((w) => w && !SKIP.has(w.toLowerCase()))
    .map((w) => w[0].toUpperCase())
    .join('');
};

// Two-pass fuzzy match of a job title against a list of ZIP filenames.
//
// Pass 1 — full title match:
//   Searches the normalised job title against normalised filenames.
//   If there's a single clear winner (score ≤ AUTO_MAP_THRESHOLD), returns it immediately.
//
// Pass 2 — abbreviation match:
//   Runs only when Pass 1 has no clear winner. Searches the job title's initialism
//   (e.g. "BE" for "Backend Engineer") to catch files named by abbreviation.
//   Merges Pass 1 + Pass 2 candidates, keeping the best score per file.
//
// Returns { candidates: [{ filename, score }], pass: 0|1|2 }
const scoreFilename = (jobTitle, filenames) => {
  const normalisedNames = filenames.map((f) => ({ original: f, normalised: normalise(f) }));

  // Pass 1 — full title fuzzy match
  const fuse1 = new Fuse(normalisedNames, {
    keys: ['normalised'],
    threshold: SUGGEST_THRESHOLD,
    includeScore: true,
    shouldSort: true,
  });
  const pass1 = fuse1.search(normalise(jobTitle));

  if (pass1.length > 0 && pass1[0].score <= AUTO_MAP_THRESHOLD) {
    // Single unambiguous winner — keep only results within 0.08 of the top score
    const topScore = pass1[0].score;
    const candidates = pass1.filter((r) => r.score - topScore < 0.08);
    return { candidates: candidates.map((r) => ({ filename: r.item.original, score: r.score })), pass: 1 };
  }

  // Pass 2 — abbreviation match (suggests only, never auto-maps)
  const abbr = buildAbbreviation(jobTitle);
  if (abbr.length >= 2) {
    const fuse2 = new Fuse(normalisedNames, {
      keys: ['normalised'],
      threshold: SUGGEST_THRESHOLD,
      includeScore: true,
      shouldSort: true,
    });
    const pass2 = fuse2.search(abbr.toLowerCase());
    if (pass2.length > 0) {
      // Merge pass1 + pass2, keeping the best (lowest) score per filename, top 5
      const merged = [...pass1, ...pass2]
        .reduce((acc, r) => {
          const existing = acc.find((x) => x.filename === r.item.original);
          if (!existing) acc.push({ filename: r.item.original, score: r.score });
          else if (r.score < existing.score) existing.score = r.score;
          return acc;
        }, [])
        .sort((a, b) => a.score - b.score)
        .slice(0, 5);
      return { candidates: merged, pass: 2 };
    }
  }

  // Pass 2 found nothing — return whatever Pass 1 had (weak matches, up to 5)
  if (pass1.length > 0) {
    return { candidates: pass1.slice(0, 5).map((r) => ({ filename: r.item.original, score: r.score })), pass: 1 };
  }

  return { candidates: [], pass: 0 };
};

const useZipMatcher = () => {
  const [zipFile, setZipFile] = useState(null);
  const [zipFilenames, setZipFilenames] = useState([]);
  const [zipError, setZipError] = useState(null);
  // rowKey → { filename, file } — populated after the user confirms mappings
  const [resolvedMappings, setResolvedMappings] = useState({});

  // Read the ZIP client-side (JSZip). Only stores filenames at this point —
  // actual file bytes are NOT read until the user confirms mappings (resolveFiles).
  const loadZip = useCallback(async (file) => {
    setZipError(null);
    setZipFilenames([]);
    setResolvedMappings({});
    try {
      const zip = await JSZip.loadAsync(file);
      const names = Object.keys(zip.files).filter(
        (name) =>
          !zip.files[name].dir &&
          ACCEPTED_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext))
      );
      if (names.length === 0) {
        setZipError('No supported files found in the ZIP (.pdf, .doc, .docx, .txt).');
        return;
      }
      setZipFile(zip);
      setZipFilenames(names);
    } catch {
      setZipError('Could not read the ZIP file. Please ensure it is a valid .zip archive.');
    }
  }, []);

  const clearZip = useCallback(() => {
    setZipFile(null);
    setZipFilenames([]);
    setZipError(null);
    setResolvedMappings({});
  }, []);

  // Run two-pass matching for every row against the ZIP filenames.
  // Returns { autoMapped: [{rowKey, filename}], needsReview: [{rowKey, jobTitle, candidates}] }
  // autoMapped = confident single match, needsReview = ambiguous or no match.
  const computeMatches = useCallback(
    (rows) => {
      if (!zipFilenames.length) return { autoMapped: [], needsReview: [] };

      const autoMapped = [];
      const needsReview = [];

      rows.forEach((row) => {
        const jobTitle = row.jobTitle || '';
        if (!jobTitle) {
          needsReview.push({ rowKey: row.key, jobTitle: '(no title)', candidates: [] });
          return;
        }
        const { candidates, pass } = scoreFilename(jobTitle, zipFilenames);
        // Auto-map only when Pass 1 returns exactly one confident result
        if (pass === 1 && candidates.length === 1 && candidates[0].score <= AUTO_MAP_THRESHOLD) {
          autoMapped.push({ rowKey: row.key, filename: candidates[0].filename });
        } else {
          needsReview.push({ rowKey: row.key, jobTitle, candidates });
        }
      });

      return { autoMapped, needsReview };
    },
    [zipFilenames]
  );

  // Extract actual file blobs from the ZIP for confirmed decisions only.
  // decisions: { rowKey: filename (string) | null (skip) }
  // Rows not present in decisions are returned unchanged.
  const resolveFiles = useCallback(
    async (rows, decisions) => {
      if (!zipFile) return rows;

      const blobCache = {};

      const getBlobFor = async (filename) => {
        if (!filename) return null;
        if (blobCache[filename]) return blobCache[filename];
        try {
          const blob = await zipFile.files[filename].async('blob');
          blobCache[filename] = new File([blob], filename.split('/').pop(), { type: blob.type });
          return blobCache[filename];
        } catch {
          return null;
        }
      };

      const updated = await Promise.all(
        rows.map(async (row) => {
          const filename = decisions[row.key];
          if (filename === undefined) return row; // not in decisions — unchanged
          const file = await getBlobFor(filename);
          return { ...row, file: file ?? null, fileError: null };
        })
      );

      const mappings = {};
      updated.forEach((r) => {
        if (r.file) mappings[r.key] = { filename: decisions[r.key], file: r.file };
      });
      setResolvedMappings(mappings);

      return updated;
    },
    [zipFile]
  );

  return {
    zipFilenames,
    zipError,
    hasZip: !!zipFile,
    loadZip,
    clearZip,
    computeMatches,
    resolveFiles,
    resolvedMappings,
  };
};

export default useZipMatcher;
